package ai.jessie.web.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.systemPreference
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val STORAGE_KEY = "jessie.colorMode"
private const val STORED_LIGHT = "light"
private const val STORED_DARK = "dark"

/**
 * Stored choice first, otherwise the operating-system preference. Presentation only: the exported
 * shell stays in the light theme.
 */
@InitSilk
fun initColorMode(ctx: InitSilkContext) {
    if (AppGlobals.isExporting) return
    ctx.config.initialColorMode =
        when (storedColorMode()) {
            STORED_DARK -> ColorMode.DARK
            STORED_LIGHT -> ColorMode.LIGHT
            else -> ColorMode.systemPreference
        }
}

private fun storedColorMode() = runCatching { localStorage.getItem(STORAGE_KEY) }.getOrNull()

private fun ColorMode.remember() {
    runCatching { localStorage.setItem(STORAGE_KEY, if (isDark) STORED_DARK else STORED_LIGHT) }
}

/** Icon-only switch in the top navigation; shows the mode it switches to. */
@Composable
fun colorModeToggle() {
    var mode by ColorMode.currentState
    val target = mode.opposite
    val label = if (target.isDark) "Dunkle Darstellung" else "Helle Darstellung"
    Button(
        attrs =
            NavIconStyle.toAttrs {
                attr("type", "button")
                attr("aria-label", label)
                attr("title", label)
                onClick {
                    mode = target
                    target.remember()
                }
            }
    ) {
        icon(if (target.isDark) "moon" else "sun", size = 18)
    }
}

/**
 * Hell / Dunkel / System for this device (`18-EinstellungenProfil.png`, Darstellung). "System"
 * forgets the stored choice and follows the operating-system preference again.
 */
@Composable
fun colorModeChoice() {
    var mode by ColorMode.currentState
    var revision by remember { mutableStateOf(0) }
    // The top-bar switch stores its choice too, so the stored value is re-read when the mode
    // changes.
    val stored = remember(mode, revision) { storedColorMode() }
    Div(
        attrs =
            SegmentedStyle.toAttrs {
                attr("role", "group")
                attr("aria-label", "Darstellung")
            }
    ) {
        segment("Hell", stored == STORED_LIGHT) {
            mode = ColorMode.LIGHT
            ColorMode.LIGHT.remember()
            revision++
        }
        segment("Dunkel", stored == STORED_DARK) {
            mode = ColorMode.DARK
            ColorMode.DARK.remember()
            revision++
        }
        segment("System", stored != STORED_LIGHT && stored != STORED_DARK) {
            runCatching { localStorage.removeItem(STORAGE_KEY) }
            mode = ColorMode.systemPreference
            revision++
        }
    }
}

@Composable
private fun segment(label: String, pressed: Boolean, choose: () -> Unit) {
    Button(
        attrs =
            SegmentStyle.toAttrs {
                attr("type", "button")
                attr("aria-pressed", pressed.toString())
                onClick { choose() }
            }
    ) {
        Text(label)
    }
}
